import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Button, Image, Header, Icon, Label, Responsive, Popup } from 'semantic-ui-react';
import i18next from 'i18next';
import { DataGrid } from '../../common';
import SubcategoriesLinks from './SubcategoriesLinks';
import { setEditedEntity, confirmDelete, deleteMenuItem, confirm, deleteOverrides } from '../../../actions';
import { getMenuItemsAppearances, getBusinessCurrency } from '../../../selectors';
import { formatPrice } from '../../../helpers/format';

class ItemsBank extends Component {
    state = {}

    handleOnUpdate = (e, { width }) => this.setState({ width })

    handleDelete = (item) => {
        this.props.confirmDelete(item.name, () => this.props.deleteMenuItem(item.menuId, item.id));
    }

    handleOverrideClick = (item) => {
        const { confirm, deleteOverrides, branchIdForOverride } = this.props;

        confirm({
            title: i18next.t('menu.syncOverrideTitle'),
            message: `${i18next.t('menu.syncOverrideMessage')}: ${Object.keys(item.branchOverrides).join(', ')}`,
            onConfirm: () => {
                deleteOverrides(item.menuId, branchIdForOverride, 'item', item.id);
            }
        });
    }

    extractItemTags = item => {
        return Object.values(item.tags).reduce((allTags, currentTag) => {
            return allTags.concat(currentTag);
        }, []);
    }

    render() {
        const columns = [{
            id: 'activityStatus',
            Header: i18next.t('menu.state'),
            maxWidth: 60,
            accessor: ({ isAvailable }) => isAvailable ? 'active' : 'inactive',
            Cell: row => {
                const statusColor = row.original.isAvailable ? 'green' : 'red';
                return <Label circular color={statusColor} empty style={{ marginLeft: 15, marginRight: 15 }} />;
            },
            filterable: true,
            filterValues: [
                { value: 'active', text: i18next.t('active') },
                { value: 'inactive', text: i18next.t('inactive') },
            ]
        }, {
            id: 'key',
            Header: i18next.t('id'),
            // Cell: ({ value: item }) => <b title={(this.props.itemsAppearances[item.id] || []).join(' | ')}>{item.key}</b>,
            filterable: true,
            maxWidth: 120,
            accessor: ({ id, key }) => ({ id, key }),
            Cell: ({ value: item }) => {
                const appearances = this.props.itemsAppearances[item.id] || [];

                return (
                    <div>
                        <Label basic className="tooltip" title={appearances.join(', ')}>{appearances.length}</Label>
                        <Label>{item.key}</Label>
                    </div>
                );
            },
        }, {
            id: 'name',
            Header: i18next.t('name'),
            // accessor: 'name',
            // Cell: row => <div>{row.value.name}</div>,
            minWidth: 150,
            filterable: true,
            accessor: ({ id, menuId, name, options, branchOverrides, imageUrl }) => ({ id, menuId, name, options, branchOverrides, imageUrl }),
            Cell: ({ value: item }) => (
                <Header as='h4' image style={{ width: '100%' }}>
                    {item.imageUrl ? <Image className={item.imageUrl ? 'zoom' : ''} floated="right" src={item.imageUrl} rounded size='mini' /> : null}
                    <Header.Content style={{ padding: 0 }}>
                        {item.name}
                        <Header.Subheader>
                            {item.options.supportedFor.includes('sitting') ? <Icon name='food' /> : null}
                            {item.options.supportedFor.includes('takeaway') ? <Icon name='shopping bag' /> : null}
                            {item.options.supportedFor.includes('delivery') ? <Icon name='motorcycle' /> : null}
                            {item.branchOverrides 
                                ? <Icon style={{ cursor: 'pointer' }} name='warning sign' color="yellow" onClick={() => this.handleOverrideClick(item)} />
                                : null}
                        </Header.Subheader>
                    </Header.Content>
                </Header>
            ),
        }, {
            Header: i18next.t('posId'),
            accessor: 'posItemId',
            filterable: true
        }, {
            id: 'tags',
            Header: i18next.t('tags.name'),
            accessor: item => {
                const tags = this.extractItemTags(item);
                
                return tags.map(tag => (
                        <Popup 
                            key={tag} 
                            trigger={<Label color="blue">{i18next.t(`tags.smartPromotions.${tag}`)}</Label>}
                            content={tags.map(t => i18next.t(`tags.smartPromotions.${t}`)).join(', ')}
                            position='top center'
                        />
                    ));
            },
            filterable: true
        }, {
            id: 'price',
            Header: i18next.t('price'),
            accessor: item => formatPrice(item.price, this.props.currencyCode),
            maxWidth: 80,
            filterable: true
        }, {
            id: 'subcategories',
            Header: i18next.t('menu.subcategories'),
            accessor: ({ subcategoriesIds }) => subcategoriesIds.map(cid => this.props.categoriesById[cid]),
            Cell: row => <SubcategoriesLinks subcategories={row.value} onSubcategoryClick={this.props.handleSubcategoryClick} />,
            style: { overflowX: 'auto', textOverflow: 'clip' },
            minWidth: 200,
            // filterable: true
        }, {
            id: 'actions',
            Header: i18next.t('actions'),
            minWidth: 110,
            maxWidth: 110,
            sortable: false,
            filterable: false,
            Cell: row => {
                const item = row.original;

                return (
                    <div>
                        <Button circular size='tiny' icon='write' basic onClick={() => this.props.setEditedEntity('menuItem', item)} />
                        <Button circular size='tiny' icon='copy' basic disabled={this.state.width <= Responsive.onlyMobile.maxWidth} onClick={() => this.props.setEditedEntity('menuItem', { ...item, id: undefined })} />
                        <Button circular size='tiny' icon='trash' negative disabled={this.props.preventItemsDeletion || this.state.width <= Responsive.onlyMobile.maxWidth} onClick={() => this.handleDelete(item)} />
                    </div>
                );
            }
        }].filter(col => {
            if (this.state.width <= Responsive.onlyMobile.maxWidth) {
                return ['name', 'actions'].includes(col.id);
            }
            return true;
        });

        return (
            <Responsive
                fireOnMount
                onUpdate={this.handleOnUpdate}
                style={{ flex: 1, padding: 10, overflowY: 'auto', paddingBottom: 150 }}
            >
                <DataGrid
                    data={this.props.items}
                    columns={columns}
                    
                    getTrGroupProps={(state, rowInfo, column) => {
                        if (rowInfo && rowInfo.original && !rowInfo.original.isAvailable) {
                            return { className: 'pi-data-grid-row-negative' };
                        }

                        return {};
                    }}
                />
            </Responsive>
        );
    }
}

ItemsBank.propTypes = {
    currencyCode: PropTypes.string,
    menuId: PropTypes.string,
    items: PropTypes.array,
    preventItemsDeletion: PropTypes.bool,
    categoriesById: PropTypes.object,
    itemsAppearances: PropTypes.object,
    setEditedEntity: PropTypes.func,
    confirmDelete: PropTypes.func,
    deleteMenuItem: PropTypes.func,
    handleSubcategoryClick: PropTypes.func,
    
    confirm: PropTypes.func,
    deleteOverrides: PropTypes.func,
    branchIdForOverride: PropTypes.string,
};

ItemsBank.defaultProps = {
    handleSubcategoryClick: (id) => console.log(id),
};

const mapStateToProps = (state) => {
    const items = Object.values(state.businessApp.menu.items);
    const { categories: categoriesById } = state.businessApp.menu;
    const currencyCode = getBusinessCurrency(state);
    const itemsAppearances = getMenuItemsAppearances(state);

    return { items, categoriesById, itemsAppearances, currencyCode };
};

export default connect(mapStateToProps, { setEditedEntity, confirmDelete, deleteMenuItem, confirm, deleteOverrides })(ItemsBank);
